/**
 * Unit page layout on the same 12-column grid as the Today page: each card has a position and a
 * width; its height always follows its content (measured on the page), so the saved `h` is only a
 * starting guess. Heights are in 2px grid rows.
 */
export type GridItem = { i: string; x: number; y: number; w: number; h: number };

// newOnTop: a card added after people saved their layout goes to the top (not the bottom), so it is noticed.
export const UNIT_CARDS: Array<{ id: string; label: string; w: number; minW: number; col: 'left' | 'right' | 'full'; px: number; newOnTop?: boolean }> = [
  { id: 'payment', label: 'Payment', w: 7, minW: 4, col: 'left', px: 300 },
  { id: 'attention', label: 'Needs attention', w: 5, minW: 3, col: 'right', px: 300 },
  { id: 'steps', label: 'Booking to possession steps', w: 12, minW: 6, col: 'full', px: 220 },
  { id: 'pending', label: 'Pending work', w: 5, minW: 3, col: 'right', px: 260, newOnTop: true },
  { id: 'lifecycle', label: 'Lifecycle', w: 7, minW: 4, col: 'left', px: 640 },
  { id: 'documents', label: 'Documents', w: 7, minW: 3, col: 'left', px: 320 },
  { id: 'changeOrders', label: 'Change orders', w: 7, minW: 3, col: 'left', px: 200 },
  { id: 'whatsapp', label: 'WhatsApp', w: 7, minW: 4, col: 'left', px: 120 },
  { id: 'collection', label: 'Collection snapshot', w: 5, minW: 4, col: 'right', px: 260 },
  { id: 'milestones', label: 'Payment milestones', w: 5, minW: 4, col: 'right', px: 300 },
  { id: 'receipts', label: 'Receipts', w: 5, minW: 4, col: 'right', px: 420 },
  { id: 'dates', label: 'Key dates', w: 5, minW: 3, col: 'right', px: 300 },
  { id: 'charges', label: 'GST and legal charges', w: 5, minW: 3, col: 'right', px: 280 },
  { id: 'loan', label: 'Home loan', w: 5, minW: 3, col: 'right', px: 200 },
  { id: 'buyers', label: 'Buyers', w: 5, minW: 3, col: 'right', px: 220 },
  { id: 'buyerForm', label: 'Buyer details form', w: 5, minW: 3, col: 'right', px: 160 },
  { id: 'areas', label: 'Areas', w: 5, minW: 3, col: 'right', px: 240 },
];
export const UNIT_CARD_BY_ID = new Map(UNIT_CARDS.map((c) => [c.id, c]));
export const cardLabel = (id: string) => UNIT_CARD_BY_ID.get(id)?.label || id;

export const GRID_ROW = 2;
export const GRID_GAP = 14;
export const rowsForPx = (px: number) => Math.ceil((px + GRID_GAP) / GRID_ROW);

/** The built-in arrangement: Payment and Needs attention on top, the step bar, then a 7 / 5 split. */
export function defaultUnitGrid(): GridItem[] {
  const out: GridItem[] = [];
  let top = 0;
  const topRow = ['payment', 'attention'];
  topRow.forEach((id) => {
    const c = UNIT_CARD_BY_ID.get(id)!;
    out.push({ i: id, x: id === 'payment' ? 0 : 7, y: 0, w: c.w, h: rowsForPx(c.px) });
    top = Math.max(top, rowsForPx(c.px));
  });
  const steps = UNIT_CARD_BY_ID.get('steps')!;
  out.push({ i: 'steps', x: 0, y: top, w: 12, h: rowsForPx(steps.px) });
  let left = top + rowsForPx(steps.px);
  let right = left;
  UNIT_CARDS.filter((c) => !topRow.includes(c.id) && c.col !== 'full').forEach((c) => {
    const h = rowsForPx(c.px);
    if (c.col === 'left') {
      out.push({ i: c.id, x: 0, y: left, w: c.w, h });
      left += h;
    } else {
      out.push({ i: c.id, x: 7, y: right, w: c.w, h });
      right += h;
    }
  });
  return out;
}

const finite = (v: unknown) => Number.isFinite(Number(v));

/** Clean a saved grid: known cards only, no duplicates, sizes inside the grid. */
export function normalizeUnitGrid(raw: any): { layout: GridItem[]; hidden: string[] } | null {
  if (!raw || !Array.isArray(raw.layout)) return null;
  const seen = new Set<string>();
  const layout: GridItem[] = [];
  raw.layout.forEach((l: any) => {
    const def = l && typeof l.i === 'string' ? UNIT_CARD_BY_ID.get(l.i) : null;
    if (!def || seen.has(l.i) || ![l.x, l.y, l.w, l.h].every(finite)) return;
    seen.add(l.i);
    const w = Math.max(def.minW, Math.min(12, Math.round(Number(l.w))));
    layout.push({ i: l.i, x: Math.max(0, Math.min(12 - w, Math.round(Number(l.x)))), y: Math.max(0, Math.round(Number(l.y))), w, h: Math.max(1, Math.round(Number(l.h))) });
  });
  const hidden = Array.isArray(raw.hidden) ? raw.hidden.filter((id: any) => typeof id === 'string' && UNIT_CARD_BY_ID.has(id) && !seen.has(id)) : [];
  return { layout, hidden };
}

/** Cards on the page: saved positions first; cards missing from the save (new since) go to the bottom. */
export function placeCards(saved: GridItem[] | null, hidden: Set<string>): GridItem[] {
  if (!saved) return defaultUnitGrid().filter((l) => !hidden.has(l.i));
  const byId = new Map(saved.map((l) => [l.i, l]));
  const onTop = UNIT_CARDS.filter((c) => c.newOnTop && !byId.has(c.id) && !hidden.has(c.id));
  const shift = onTop.reduce((m, c) => m + rowsForPx(c.px), 0);
  let top = 0;
  let bottom = saved.reduce((m, l) => Math.max(m, l.y + l.h), 0) + shift;
  return UNIT_CARDS.filter((c) => !hidden.has(c.id)).map((c) => {
    const own = byId.get(c.id);
    // The grid compacts upward, so cards pushed down by a new top card close the gap again where there is room.
    if (own) return shift ? { ...own, y: own.y + shift } : own;
    if (c.newOnTop) {
      const atTop = { i: c.id, x: c.col === 'right' ? 12 - c.w : 0, y: top, w: c.w, h: rowsForPx(c.px) };
      top += atTop.h;
      return atTop;
    }
    const placed = { i: c.id, x: 0, y: bottom, w: c.w, h: rowsForPx(c.px) };
    bottom += placed.h;
    return placed;
  });
}
